/**
 * UTM Tracking 
 * Key Features:
 * - First-touch attribution: Preserves original campaign source
 * - Last-touch attribution: Tracks most recent campaign interaction
*/

// Types & Constants

export type UTMParameter = 'utm_source' | 'utm_medium' | 'utm_campaign';

// Cookie attribution type

export type AttributionType = 'first' | 'last';

// UTM parameters object structure
 
export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

//  Configuration for cookie settings
 
export interface CookieConfig {
  maxAge: number;        // Cookie lifetime in seconds (365 days default)
  path: string;          // Cookie path (root default)
  domain?: string;       // Cookie domain (for cross-subdomain support)
  sameSite: 'Lax' | 'Strict' | 'None';  // SameSite policy
  secure: boolean;       // Secure flag (HTTPS only)
}

/**
 * Default cookie configuration
 * - domain: .resumemate.io (with leading dot for cross-subdomain support)
 * - Only set domain in production (not localhost)
  */
const DEFAULT_COOKIE_CONFIG: CookieConfig = {
  maxAge: 365 * 24 * 60 * 60, // 365 days in seconds
  path: '/',
  domain: typeof window !== 'undefined' && !window.location.hostname.includes('localhost') 
    ? '.resumemate.io' 
    : undefined,
  sameSite: 'Lax',
  secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
};

//  List of supported UTM parameters

const UTM_PARAMETERS: UTMParameter[] = ['utm_source', 'utm_medium', 'utm_campaign'];

// ============================================================================
// Cookie Utility Functions
// ============================================================================

/**
 *  get a cookie value by name
 * Returns null if cookie doesn't exist or has invalid value
 * 
 * @param name - Cookie name to retrieve
 * @returns Cookie value or null
 * 
 * @example
 * const source = getCookie('first_utm_source');
 * if (source) {
 *   console.log('First touch source:', source);
 * }
 */
export function getCookie(name: string): string | null {
  try {
    // Early return for server-side rendering
    if (typeof document === 'undefined') {
      return null;
    }

    // Parse cookies from document
    const cookies = document.cookie.split(';');
    
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split('=').map(c => c.trim());
      
      if (cookieName === name) {
        const decodedValue = decodeURIComponent(cookieValue);
        
        // Validate that value is not empty, null, undefined
        if (decodedValue && 
            decodedValue !== 'null' && 
            decodedValue !== 'undefined' && 
            decodedValue.length > 0) {
          return decodedValue;
        }
      }
    }
    
    return null;
  } catch (error) {
    // Fail silently - never throw errors
    console.error('[UTM Tracking] Error reading cookie:', name, error);
    return null;
  }
}

/**
 * set a cookie with validation
 * Only sets cookie if value is valid (not null, undefined, or empty)
 * 
 * @param name - Cookie name
 * @param value - Cookie value (only set if valid)
 * @param config - Optional cookie configuration
 * @returns true if cookie was set, false otherwise
 * 
 * @example
 * setCookie('first_utm_source', 'google', { maxAge: 86400 });
 */
export function setCookie(
  name: string, 
  value: string | null | undefined, 
  config: Partial<CookieConfig> = {}
): boolean {
  try {
    // Early return for server-side rendering
    if (typeof document === 'undefined') {
      return false;
    }

    if (!value || value.trim().length === 0 || value === 'null' || value === 'undefined') {
      console.warn('[UTM Tracking] Attempted to set invalid cookie value:', name, value);
      return false;
    }

    // Merge with default config
    const finalConfig: CookieConfig = { ...DEFAULT_COOKIE_CONFIG, ...config };

    // Build cookie string
    const cookieParts = [
      `${name}=${encodeURIComponent(value)}`,
      `max-age=${finalConfig.maxAge}`,
      `path=${finalConfig.path}`,
      `samesite=${finalConfig.sameSite}`,
    ];

    // Add domain if specified (for cross-subdomain support)
    if (finalConfig.domain) {
      cookieParts.push(`domain=${finalConfig.domain}`);
    }

    // Add secure flag if enabled
    if (finalConfig.secure) {
      cookieParts.push('secure');
    }

    // Set cookie
    document.cookie = cookieParts.join('; ');
    
    // Verify cookie was set
    const verifyValue = getCookie(name);
    if (verifyValue === value) {
      return true;
    }
    
    console.warn('[UTM Tracking] Cookie verification failed:', name);
    return false;
  } catch (error) {
    console.error('[UTM Tracking] Error setting cookie:', name, error);
    return false;
  }
}

/**
 * Check if a cookie exists and has a valid value
 * 
 * @param name - Cookie name to check
 * @returns true if cookie exists with valid value
 * 
 * @example
 * if (hasCookie('first_utm_source')) {
 *   console.log('First-touch source already tracked');
 * }
 */
export function hasCookie(name: string): boolean {
  const value = getCookie(name);
  return value !== null && value.length > 0;
}

// ============================================================================
// UTM Parameter Extraction
// ============================================================================

/**
 * Extract UTM parameters from current URL
 * Only returns parameters that have valid values
 * // URL: https://example.com?utm_source=google&utm_medium=cpc
 * const params = extractUTMFromURL();
 * // Returns: { utm_source: 'google', utm_medium: 'cpc' }
 */
export function extractUTMFromURL(): UTMParams {
  try {
    // Early return for server-side rendering
    if (typeof window === 'undefined') {
      return {};
    }

    const params: UTMParams = {};
    const urlParams = new URLSearchParams(window.location.search);

    // Extract each UTM parameter if it has a valid value
    for (const param of UTM_PARAMETERS) {
      const value = urlParams.get(param);
      
      if (value && value.trim().length > 0 && value !== 'null' && value !== 'undefined') {
        params[param] = value.trim();
      }
    }

    return params;
  } catch (error) {
    console.error('[UTM Tracking] Error extracting UTM from URL:', error);
    return {};
  }
}

/**
 * Check if URL has any valid UTM parameters
 * 
 * @returns true if at least one valid UTM parameter exists
 */
export function hasUTMParameters(): boolean {
  const params = extractUTMFromURL();
  return Object.keys(params).length > 0;
}

// ============================================================================
// Attribution Cookie Management
// ============================================================================

/**
 * Get cookie name for a specific UTM parameter and attribution type
 * 
 * @param param - UTM parameter name
 * @param type - Attribution type (first or last)
 * @returns Cookie name
 * 
 * @example
 * getCookieName('utm_source', 'first') // Returns: 'first_utm_source'
 * getCookieName('utm_campaign', 'last') // Returns: 'last_utm_campaign'
 */
function getCookieName(param: UTMParameter, type: AttributionType): string {
  return `${type}_${param}`;
}

/**
 * Set first-touch attribution cookie
 * Only sets cookie if it doesn't already exist (immutable first-touch)
 * 
 * @param param - UTM parameter name
 * @param value - Parameter value
 * @returns true if cookie was set, false if already exists or invalid
 */
export function setFirstTouchCookie(param: UTMParameter, value: string | null | undefined): boolean {
  const cookieName = getCookieName(param, 'first');
  
  // Check if first-touch cookie already exists
  if (hasCookie(cookieName)) {
    // First-touch attribution is immutable - do not overwrite
    return false;
  }

  // Validate value before setting
  if (!value || value.trim().length === 0) {
    return false;
  }

  // Set first-touch cookie (365 days)
  return setCookie(cookieName, value);
}

/**
 * Set last-touch attribution cookie
 * Always updates with the most recent value (mutable last-touch)
 * 
 * @param param - UTM parameter name
 * @param value - Parameter value
 * @returns true if cookie was set, false if invalid
 * 
 */
export function setLastTouchCookie(param: UTMParameter, value: string | null | undefined): boolean {
  const cookieName = getCookieName(param, 'last');
  
  // Validate value before setting
  if (!value || value.trim().length === 0) {
    return false;
  }

  // Always update last-touch cookie (365 days)
  return setCookie(cookieName, value);
}

/**
 * Get first-touch attribution value for a UTM parameter
*/
export function getFirstTouchValue(param: UTMParameter): string | null {
  return getCookie(getCookieName(param, 'first'));
}


  // Get last-touch attribution value for a UTM parameter

export function getLastTouchValue(param: UTMParameter): string | null {
  return getCookie(getCookieName(param, 'last'));
}


  // Get all first-touch attribution values
 
export function getAllFirstTouchValues(): UTMParams {
  return {
    utm_source: getFirstTouchValue('utm_source') || undefined,
    utm_medium: getFirstTouchValue('utm_medium') || undefined,
    utm_campaign: getFirstTouchValue('utm_campaign') || undefined,
  };
}

// Get all last-touch attribution values
export function getAllLastTouchValues(): UTMParams {
  return {
    utm_source: getLastTouchValue('utm_source') || undefined,
    utm_medium: getLastTouchValue('utm_medium') || undefined,
    utm_campaign: getLastTouchValue('utm_campaign') || undefined,
  };
}

// ============================================================================
// Main Attribution Tracking Function
// ============================================================================

export function trackUTMAttribution(): {
  firstTouchSet: boolean;
  lastTouchSet: boolean;
  params: UTMParams;
} {
  try {
    // Extract UTM parameters from URL
    const utmParams = extractUTMFromURL();
    
    // Track if any cookies were set
    let firstTouchSet = false;
    let lastTouchSet = false;

    // Process each UTM parameter
    for (const param of UTM_PARAMETERS) {
      const value = utmParams[param];
      
       if (!value || value.trim().length === 0) {
        continue;
      }

      // Set first-touch cookie (only if doesn't exist)
      const firstSet = setFirstTouchCookie(param, value);
      if (firstSet) {
        firstTouchSet = true;
      }

      // Always update last-touch cookie
      const lastSet = setLastTouchCookie(param, value);
      if (lastSet) {
        lastTouchSet = true;
      }
    }

    return {
      firstTouchSet,
      lastTouchSet,
      params: utmParams,
    };
  } catch (error) {
    console.error('[UTM Tracking] Error in trackUTMAttribution:', error);
    return {
      firstTouchSet: false,
      lastTouchSet: false,
      params: {},
    };
  }
}

// ============================================================================
// Debug & Utility Functions
// ============================================================================

export function getAttributionData() {
  return {
    current: extractUTMFromURL(),
    firstTouch: getAllFirstTouchValues(),
    lastTouch: getAllLastTouchValues(),
  };
}

/**
 * Log current attribution status (for debugging)
 * Only logs in development environment
 */
export function debugAttributionStatus(): void {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'production') {
    return;
  }

  const data = getAttributionData();
  
  console.group('🎯 UTM Attribution Status');
  console.log('Current URL Params:', data.current);
  console.log('First-Touch Attribution:', data.firstTouch);
  console.log('Last-Touch Attribution:', data.lastTouch);
  console.groupEnd();
}
